import { useQuery } from '@tanstack/react-query';
import type { Feature, Position } from 'geojson';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { t } from '@/lib/i18n';
import type { LiveBus } from '@/lib/live-buses';
import { useNetwork } from '@/lib/network';
import { checkZone, travelOrdered } from '@/lib/zone-check';
import { arrowIcon } from './map/icons';
import { arrowLayer, fc, GeoJsonLayer, lineLayer } from './map/layers';
import { LiveBusesLayer } from './map/live-buses';
import { MapTag } from './map/map-tag';
import { boundsOf, MapView } from './map/map-view';
import { Input, Select } from './ui/form';
import { Card, Spinner } from './ui/layout';

type Dir = 'going' | 'return' | null;
interface TrackPoint {
  t: string;
  c: Position;
  legacy: Dir;
  v2: Dir;
  off: boolean;
  fence: boolean;
  speed: number;
}
interface Track {
  points: TrackPoint[];
  truncated: boolean;
}

const DIR_COLORS = { going: '#2563eb', return: '#ea580c', none: '#94a3b8' };

/** A gap in the data longer than this breaks the drawn route. */
const GAP_MS = 2 * 60_000;

/**
 * The bus page map: where the bus is, where it drove recently (coloured by the direction that was
 * detected), and its zone's lines with A and B. Optionally compares with the old fence rule.
 */
export function BusMap({ busId, zoneId, bus }: { busId: string; zoneId?: string; bus?: LiveBus }) {
  const network = useNetwork();
  const [minutes, setMinutes] = useState(30);
  const [at, setAt] = useState('');
  const [source, setSource] = useState<'live' | 'replay'>('live');
  const [compare, setCompare] = useState(false);
  const query = useMemo(() => {
    if (!at) return { minutes, source };
    const to = new Date(at);
    return { from: new Date(to.getTime() - minutes * 60_000).toISOString(), to: to.toISOString(), source };
  }, [minutes, at, source]);
  const track = useQuery({
    queryKey: ['buses', 'track', busId, query],
    queryFn: () => api<Track>(`/buses/${busId}/track`, { query }),
    refetchInterval: at ? false : 15_000,
  });

  const data = useMemo(() => {
    const pts = track.data?.points ?? [];
    const segments: Feature[] = [];
    const differ: Feature[] = [];
    for (let i = 1; i < pts.length; i++) {
      const [a, b] = [pts[i - 1]!, pts[i]!];
      if (Date.parse(b.t) - Date.parse(a.t) > GAP_MS) continue;
      const segment: Feature = {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [a.c, b.c] },
        properties: { color: b.off ? DIR_COLORS.none : DIR_COLORS[b.v2 ?? 'none'] },
      };
      segments.push(segment);
      if (b.legacy && b.v2 && b.legacy !== b.v2 && !b.off) differ.push(segment);
    }
    let agree = 0;
    let differs = 0;
    for (const p of pts)
      if (p.legacy && p.v2 && !p.off) {
        if (p.legacy === p.v2) agree += 1;
        else differs += 1;
      }
    const turns = (k: 'legacy' | 'v2') =>
      pts.filter((p, i) => i > 0 && p[k] && pts[i - 1]![k] && p[k] !== pts[i - 1]![k]).length;
    return {
      segments: fc(segments),
      differ: fc(differ),
      agreePct: agree + differs ? Math.round((agree / (agree + differs)) * 100) : null,
      turnsLegacy: turns('legacy'),
      turnsV2: turns('v2'),
      last: pts.at(-1),
      positions: pts.map((p) => p.c),
    };
  }, [track.data]);

  // The zone's lines in travel order, A/B, and whether its fences sit where they belong.
  const zone = network.data?.zones.find((z) => z._id === zoneId);
  const zoneView = useMemo(() => {
    if (!zone || !network.data) return null;
    const stations = new Map(network.data.stations.map((s) => [s._id, s]));
    const line = (id?: string) => {
      const l = network.data!.lines.find((x) => x._id === id);
      return fc(
        l
          ? [
              {
                type: 'Feature',
                geometry: { type: 'LineString', coordinates: travelOrdered(l, stations) },
                properties: {},
              },
            ]
          : [],
      );
    };
    const check = checkZone(zone, network.data);
    return {
      going: line(zone.on_line_going),
      return: line(zone.on_line_return),
      check,
      fencesOk: (check.going?.ok ?? true) && (check.return?.ok ?? true),
    };
  }, [zone, network.data]);

  const fit = boundsOf(
    data.positions,
    zoneView?.going.features.map((f) => (f.geometry as { coordinates: Position[] }).coordinates),
  );
  const directed = (color: string) => [
    {
      type: 'line' as const,
      layout: { 'line-cap': 'round' as const, 'line-join': 'round' as const },
      paint: { 'line-color': color, 'line-width': 3, 'line-opacity': 0.3, 'line-offset': 4 },
    },
    arrowLayer(arrowIcon(color)),
  ];

  return (
    <Card
      title={t('Map and recent route')}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Select className="w-28" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
            {[15, 30, 60, 120, 360, 720].map((m) => (
              <option key={m} value={m}>
                {m < 60 ? t('{n} min', { n: m }) : t('{n} h', { n: m / 60 })}
              </option>
            ))}
          </Select>
          <Input
            type="datetime-local"
            className="w-52"
            value={at}
            onChange={(e) => setAt(e.target.value)}
            aria-label={t('Until')}
            title={t('Until (empty: now, updated live)')}
          />
        </div>
      }
      padded={false}
    >
      <p className="border-b border-slate-200 px-4 py-2.5 text-xs leading-relaxed text-slate-600">
        {t('The coloured line is where the bus drove in this period, in the direction the system detected:')}{' '}
        <span className="font-semibold" style={{ color: DIR_COLORS.going }}>
          {t('blue = going (A → B)')}
        </span>
        ,{' '}
        <span className="font-semibold" style={{ color: DIR_COLORS.return }}>
          {t('orange = return (B → A)')}
        </span>
        , <span className="font-semibold text-slate-500">{t('grey = off route')}</span>.{' '}
        {t('The faint lines with arrows are the two directions of the line.')}
        {data.last && (
          <span className="text-slate-400">
            {' '}
            {t('Last position {time}.', { time: formatDateTime(data.last.t) })}
          </span>
        )}
      </p>
      {zoneView && !zoneView.fencesOk && zone && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          {t('The fences of this line do not sit at A and B.')}{' '}
          <Link to={`/lines/${zone._id}`} className="font-semibold underline">
            {t('Check the line')}
          </Link>
        </div>
      )}
      <div className="relative">
        {track.isLoading && (
          <div className="absolute inset-0 z-10 bg-white/60">
            <Spinner />
          </div>
        )}
        <MapView
          key={`${source}|${at}|${minutes}|${zoneId}`}
          className="h-[620px] rounded-none border-0"
          fit={fit}
        >
          {zoneView && (
            <>
              <GeoJsonLayer data={zoneView.going} layers={directed(DIR_COLORS.going)} />
              <GeoJsonLayer data={zoneView.return} layers={directed(DIR_COLORS.return)} />
            </>
          )}
          {compare && <GeoJsonLayer data={data.differ} layers={[lineLayer('#dc2626', 12, 0.3)]} />}
          <GeoJsonLayer data={data.segments} layers={[lineLayer(['get', 'color'], 4, 0.9)]} />
          {bus?.geometry && <LiveBusesLayer buses={[bus]} showHidden />}
          {zoneView?.check.a && (
            <MapTag at={zoneView.check.a.pos} color={DIR_COLORS.going}>
              A · {zoneView.check.a.name}
            </MapTag>
          )}
          {zoneView?.check.b && (
            <MapTag at={zoneView.check.b.pos} color={DIR_COLORS.return}>
              B · {zoneView.check.b.name}
            </MapTag>
          )}
        </MapView>
      </div>
      {track.data && !track.data.points.length && (
        <p className="px-4 py-2 text-sm text-slate-500">{t('No positions in this period.')}</p>
      )}
      {track.data?.truncated && (
        <p className="px-4 py-2 text-xs text-amber-700">{t('Only the first 20,000 positions are shown.')}</p>
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-200 px-4 py-2.5 text-xs text-slate-600">
        <label className="flex cursor-pointer items-center gap-1.5">
          <input
            type="checkbox"
            checked={compare}
            onChange={(e) => setCompare(e.target.checked)}
            className="rounded border-slate-300"
          />
          {t('Compare with the old logic (terminus fences only)')}
        </label>
        {compare && (
          <>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-5 rounded bg-red-600/30" />{' '}
              {t('the old logic chose the other direction')}
            </span>
            {data.agreePct !== null && <span>{t('Agreement {n}%', { n: data.agreePct })}</span>}
            <span>{t('Turns: legacy {a} · v2 {b}', { a: data.turnsLegacy, b: data.turnsV2 })}</span>
            <Select
              className="ml-auto w-36"
              value={source}
              onChange={(e) => setSource(e.target.value as 'live' | 'replay')}
            >
              <option value="live">{t('Recent logs')}</option>
              <option value="replay">{t('Replay copy')}</option>
            </Select>
          </>
        )}
      </div>
    </Card>
  );
}
